/**
 * Catalog tags of models, read from their catalog documents, for what needs a model's tags before
 * or without its web bundle: the plan's door and window symbols, which the app picks by tag. A
 * model whose conversion failed, or has not finished, still has its document.
 *
 * Every id asked in one turn goes to the catalog in one request (`fetchModels` batches them);
 * answers are kept for the session and announced when they arrive. A model the catalog does not
 * have, or a request that failed, answers no tags and is not asked again: the caller keeps its
 * fallback, and a failing catalog is not asked on every frame.
 */
import { fetchModels } from './catalogApi';

const answers = new Map<string, readonly string[] | null>();
const listeners = new Set<() => void>();
const GATHER_MS = 100;
let queue: string[] = [];
let scheduled = false;
let version = 0;

/** Grows each time tags arrive: what was worked out from `cachedModelTags` before may read differently now. */
export function modelTagsVersion(): number { return version; }

export function subscribeModelTags(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** The model's tags when they are known, `undefined` while they are on their way (asking, when nobody has yet). */
export function knownModelTags(id: string): readonly string[] | undefined {
  if (!id) return undefined;
  const known = answers.get(id);
  if (known !== undefined) return known ?? undefined;
  if (!queue.includes(id)) queue.push(id);
  if (!scheduled) {
    scheduled = true;
    setTimeout(() => { scheduled = false; void flush(queue.splice(0)); }, GATHER_MS);
  }
  return undefined;
}

/** Tags already in hand — a catalog card's — so a model just placed is drawn by them without asking the catalog. */
export function rememberModelTags(id: string, tags: readonly string[]): void {
  if (!id || !tags.length || answers.get(id)) return;
  answers.set(id, [...tags]);
  version++; for (const listener of listeners) listener();
}

/** The model's tags if an earlier `knownModelTags` has brought them, without asking the catalog: for pure level edits. */
export function cachedModelTags(id: string): readonly string[] | undefined {
  return answers.get(id) ?? undefined;
}

async function flush(ids: string[]): Promise<void> {
  for (const id of ids) answers.set(id, null);
  let documents: Awaited<ReturnType<typeof fetchModels>>;
  try { documents = await fetchModels(ids); } catch { return; }
  let arrived = false;
  for (const id of ids) {
    const tags = documents[id]?.tags;
    if (tags?.length) { answers.set(id, tags); arrived = true; }
  }
  if (arrived) { version++; for (const listener of listeners) listener(); }
}
